'use client';

import { use, useState, useMemo } from 'react';
import {
    GraduationCap,
    Search,
    Filter,
    Plus,
    MoreHorizontal,
    Mail,
    Phone,
    CheckCircle2,
    Clock,
    AlertCircle,
    ArrowLeft,
    Download,
    ChevronRight,
    UserCheck,
    Users,
    History,
    Receipt,
    Calendar,
    X,
    CreditCard,
    Eye,
    Edit2,
    Calculator,
    Coins,
    Building2
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import {
    getSchoolData,
    getSchoolName,
    SchoolUnit,
    Student,
    PaymentHistory
} from '@/lib/data';

export default function SiswaDataPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit } = use(params);
    const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'smp', 'smk'];

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    const schoolUnit = unit as SchoolUnit;
    const schoolData = getSchoolData(schoolUnit);
    const schoolName = getSchoolName(schoolUnit);

    // Dynamic data fetching - handle the unit split for data mapping
    const baseUnit = unit.split('-')[0];
    const studentList = ((schoolData as any)[`${baseUnit}Students`] || []) as Student[];

    // State
    const [searchTerm, setSearchTerm] = useState('');
    const [filterClass, setFilterClass] = useState('All');
    const [filterTuition, setFilterTuition] = useState('All');
    const [filterSchool, setFilterSchool] = useState(unit.includes('-') ? unit : 'Semua Sekolah');
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

    // Get school list for the unit
    const schools = useMemo(() => {
        const baseUnit = unit.split('-')[0];
        const schoolList = (schoolData as any)[`${baseUnit}Schools`] || [];
        return ['Semua Sekolah', ...schoolList.map((s: any) => s.id)];
    }, [schoolData, unit]);

    const getSchoolNameById = (id: string) => {
        if (id === 'Semua Sekolah') return 'Semua Sekolah';
        const baseUnit = unit.split('-')[0];
        const schoolList = (schoolData as any)[`${baseUnit}Schools`] || [];
        const school = schoolList.find((s: any) => s.id === id);
        return school ? school.name : id;
    };

    // Derived Data
    const classes = ['All', ...Array.from(new Set(studentList.map((s: Student) => s.class)))];
    const tuitionStatuses = ['All', 'paid', 'pending', 'overdue'];

    const filteredStudents = useMemo(() => {
        return studentList.filter((s: Student) => {
            const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.id.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesClass = filterClass === 'All' || s.class === filterClass;
            const matchesTuition = filterTuition === 'All' || s.tuitionStatus === filterTuition;
            const matchesSchool = filterSchool === 'Semua Sekolah' || s.schoolId === filterSchool;
            return matchesSearch && matchesClass && matchesTuition && matchesSchool;
        });
    }, [studentList, searchTerm, filterClass, filterTuition, filterSchool]);

    const stats = useMemo(() => {
        const currentStudentList = studentList.filter((s: Student) => filterSchool === 'Semua Sekolah' || s.schoolId === filterSchool);
        const parseVal = (s: string | undefined) => s ? parseInt(s.replace(/[^\d]/g, '')) : 0;
        const totalTagihan = currentStudentList.reduce((acc, curr) => acc + parseVal(curr.totalBill), 0);
        const totalPiutang = currentStudentList.reduce((acc, curr) => acc + parseVal(curr.receivable), 0);

        return {
            total: currentStudentList.length,
            active: currentStudentList.filter((s: Student) => s.status === 'active').length,
            paid: currentStudentList.filter((s: Student) => s.tuitionStatus === 'paid').length,
            pending: currentStudentList.filter((s: Student) => s.tuitionStatus !== 'paid').length,
            totalTagihan: `Rp ${totalTagihan.toLocaleString('id-ID')}`,
            totalPiutang: `Rp ${totalPiutang.toLocaleString('id-ID')}`,
        };
    }, [studentList, filterSchool]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700 relative">
            {/* Payment History Side Panel (Drawer) */}
            {selectedStudent && (
                <div className="fixed inset-0 z-100 flex justify-end animate-in fade-in duration-300">
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSelectedStudent(null)}></div>
                    <div className="relative w-full max-w-md bg-surface h-full shadow-2xl border-l border-border-main animate-in slide-in-from-right duration-500 overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-border-main flex items-center justify-between bg-primary-soft/30">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/20">
                                    <History className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg">Riwayat Pembayaran</h3>
                                    <p className="text-[10px] font-bold text-primary uppercase tracking-widest">{selectedStudent.name}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedStudent(null)} className="p-2 hover:bg-white rounded-full transition-colors">
                                <X className="w-5 h-5 text-muted" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                            {selectedStudent.paymentHistory && selectedStudent.paymentHistory.length > 0 ? (
                                selectedStudent.paymentHistory.map((history: PaymentHistory) => (
                                    <div key={history.id} className="bg-background border border-border-main rounded-3xl p-5 space-y-4 hover:border-primary/30 transition-all group">
                                        <div className="flex justify-between items-start">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-3.5 h-3.5 text-muted" />
                                                    <span className="text-xs font-bold text-foreground">{history.date}</span>
                                                </div>
                                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">No. Kwitansi: {history.receiptNo}</p>
                                            </div>
                                            <span className={`px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider ${history.status === 'success' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'
                                                }`}>
                                                {history.status}
                                            </span>
                                        </div>
                                        <div className="pt-2 border-t border-border-main/50 flex items-center justify-between">
                                            <div>
                                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Metode</p>
                                                <p className="text-xs font-bold flex items-center gap-1.5 capitalize">
                                                    <CreditCard className="w-3.5 h-3.5 text-primary" />
                                                    {history.method}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Jumlah</p>
                                                <p className="text-base font-bold text-primary tabular-nums">{history.amount}</p>
                                            </div>
                                        </div>
                                        <button className="w-full py-2.5 bg-surface border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest text-secondary hover:bg-primary hover:text-white hover:border-primary transition-all flex items-center justify-center gap-2">
                                            <Receipt className="w-4 h-4" /> Download Invoce
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-50 py-20">
                                    <Receipt className="w-16 h-16 text-muted" />
                                    <p className="text-sm font-bold text-muted">Belum ada riwayat pembayaran terekam</p>
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-border-main bg-background/50">
                            <button className="w-full bg-primary text-white py-4 rounded-2xl text-[11px] font-bold uppercase tracking-widest shadow-xl shadow-primary/10 flex items-center justify-center gap-3">
                                <Plus className="w-4 h-4" /> Input Pembayaran Baru
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Card based on Image 1 */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">

                <div className="relative z-10 space-y-10">
                    <div className="flex flex-col gap-8">
                        <Link
                            href={`/yayasan/${unit}`}
                            className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest hover:text-primary transition-all group/back"
                        >
                            <ArrowLeft className="w-3.5 h-3.5 group-hover/back:-translate-x-1 transition-transform" />
                            Kembali ke Dashboard Unit
                        </Link>

                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                    <GraduationCap className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Data Siswa</h1>
                                    <div className="flex items-center gap-2">
                                        <p className="text-secondary font-medium">{schoolName}</p>
                                        <span className="w-1.5 h-1.5 rounded-full bg-border-main"></span>
                                        <p className="text-xs font-bold text-muted uppercase tracking-widest">NPSN: 12345679</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <button className="px-6 py-3.5 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-white hover:border-primary transition-all shadow-sm flex items-center gap-2 uppercase tracking-widest">
                                    <Download className="w-4 h-4 text-muted" /> Export Excel
                                </button>

                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Grid Expansion */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-3 gap-6">
                {[
                    { label: 'Total Siswa', value: stats.total, icon: Users, color: 'text-primary', bg: 'bg-primary-soft' },
                    { label: 'Siswa Aktif', value: stats.active, icon: UserCheck, color: 'text-success', bg: 'bg-success-soft' },
                    { label: 'Total Tagihan', value: stats.totalTagihan, icon: Calculator, color: 'text-info', bg: 'bg-info-soft' },
                    { label: 'Total Piutang', value: stats.totalPiutang, icon: Coins, color: 'text-error', bg: 'bg-error-soft' },
                    { label: 'Lunas SPP', value: stats.paid, icon: CheckCircle2, color: 'text-info', bg: 'bg-info-soft' },
                    { label: 'Tunggakan', value: stats.pending, icon: AlertCircle, color: 'text-error', bg: 'bg-error-soft' },
                ].map((item, idx) => (
                    <div key={idx} className="bg-surface p-6 rounded-4xl border border-border-main shadow-sm group hover:shadow-md transition-all relative overflow-hidden">
                        <div className="flex items-center gap-4 relative z-10">
                            <div className={`w-12 h-12 ${item.bg} ${item.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                <item.icon className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">{item.label}</p>
                                <p className="text-xl font-bold text-foreground tracking-tight whitespace-nowrap">{item.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* Table Section */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden min-h-[500px] flex flex-col">
                {/* Search & Filter Bar */}
                <div className="p-6 border-b border-border-main flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari nama atau NISN..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 bg-background border border-border-main px-4 py-2 rounded-2xl">
                            <Building2 className="w-4 h-4 text-muted" />
                            <span className="text-[10px] font-bold text-muted uppercase tracking-widest border-r border-border-main pr-3 mr-1">Sekolah</span>
                            <select
                                className="bg-transparent border-none text-xs font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterSchool}
                                onChange={(e) => setFilterSchool(e.target.value)}
                            >
                                {schools.map(sId => (
                                    <option key={sId} value={sId}>{getSchoolNameById(sId)}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2 bg-background border border-border-main px-4 py-2 rounded-2xl">
                            <Filter className="w-4 h-4 text-muted" />
                            <span className="text-[10px] font-bold text-muted uppercase tracking-widest border-r border-border-main pr-3 mr-1">Kelas</span>
                            <select
                                className="bg-transparent border-none text-xs font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterClass}
                                onChange={(e) => setFilterClass(e.target.value)}
                            >
                                {classes.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2 bg-background border border-border-main px-4 py-2 rounded-2xl">
                            <span className="text-[10px] font-bold text-muted uppercase tracking-widest border-r border-border-main pr-3 mr-1">Status SPP</span>
                            <select
                                className="bg-transparent border-none text-xs font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterTuition}
                                onChange={(e) => setFilterTuition(e.target.value)}
                            >
                                {tuitionStatuses.map(status => (
                                    <option key={status} value={status}>{status.toUpperCase()}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto grow">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Identitas Siswa</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Kelas & Akademik</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Status Pembayaran</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Total Tagihan</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Piutang</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {filteredStudents.length > 0 ? filteredStudents.map((siswa: Student) => (
                                <tr key={siswa.id} className="hover:bg-primary-soft/10 transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary/10 to-primary/20 flex items-center justify-center text-primary font-bold text-xs uppercase shadow-inner border border-primary/5">
                                                {siswa.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{siswa.name}</p>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <p className="text-[9px] text-muted font-bold tabular-nums pr-1.5 border-r border-border-main leading-none">NISN: {siswa.id}</p>
                                                    <p className="text-[9px] text-primary font-bold uppercase tracking-wider leading-none">Wali: {siswa.parentName || '-'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-foreground">{siswa.class}</p>
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                                                <p className="text-[10px] text-secondary font-medium uppercase tracking-wider">Tahun Ajaran 2024/2025</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex flex-col gap-1 cursor-pointer group/tuition" onClick={() => setSelectedStudent(siswa)}>
                                            <div className={`flex items-center gap-2 w-fit px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider border transition-all group-hover/tuition:shadow-md ${siswa.tuitionStatus === 'paid'
                                                ? 'bg-success-soft text-success border-success/10'
                                                : siswa.tuitionStatus === 'pending'
                                                    ? 'bg-warning-soft text-warning border-warning/10'
                                                    : 'bg-error-soft text-error border-error/10'
                                                }`}>
                                                {siswa.tuitionStatus === 'paid' ? <CheckCircle2 className="w-3 h-3" /> : siswa.tuitionStatus === 'pending' ? <Clock className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                                {siswa.tuitionStatus}
                                                <History className="w-3 h-3 ml-1 opacity-0 group-hover/tuition:opacity-100 transition-opacity" />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 text-sm font-bold text-foreground tabular-nums whitespace-nowrap">
                                        {siswa.totalBill}
                                    </td>
                                    <td className="px-8 py-5 text-sm font-bold text-error tabular-nums whitespace-nowrap">
                                        {siswa.receivable}
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end gap-2">
                                            <div className="relative group/tooltip">
                                                <Link
                                                    href={`/yayasan/${unit}/siswa/${siswa.id}`}
                                                    className="w-9 h-9 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-primary hover:border-primary transition-all shadow-sm hover:shadow-md active:scale-95"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Link>
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-foreground text-surface text-[10px] font-bold rounded-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-xl z-50">
                                                    Detail & Riwayat
                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-foreground"></div>
                                                </div>
                                            </div>

                                            <div className="relative group/tooltip">
                                                <button className="w-9 h-9 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-info hover:border-info transition-all shadow-sm hover:shadow-md active:scale-95">
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-8 py-20 text-center">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="w-20 h-20 bg-background rounded-3xl flex items-center justify-center text-muted shadow-inner relative">
                                                <GraduationCap className="w-10 h-10 opacity-20" />
                                                <div className="absolute top-0 right-0 w-6 h-6 bg-red-500 rounded-full border-4 border-surface -translate-y-1/4 translate-x-1/4"></div>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-sm text-foreground font-bold">Data tidak ditemukan</p>
                                                <p className="text-xs text-muted max-w-[200px]">Cobalah mengubah filter atau kata kunci pencarian Anda.</p>
                                            </div>
                                            <button
                                                onClick={() => { setSearchTerm(''); setFilterClass('All'); setFilterTuition('All'); }}
                                                className="mt-2 text-[10px] font-bold text-primary uppercase tracking-widest hover:underline"
                                            >
                                                Reset Semua Filter
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer / Pagination */}
                <div className="p-6 border-t border-border-main flex flex-col sm:flex-row items-center justify-between bg-background/10 gap-4">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">
                        Menampilkan <span className="text-foreground">{filteredStudents.length}</span> dari <span className="text-foreground">{stats.total}</span> Siswa Terdaftar
                    </p>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] font-bold text-muted uppercase tracking-widest mr-2">Halaman</span>
                            {[1, 2, 3].map(p => (
                                <button key={p} className={`w-8 h-8 rounded-lg text-[10px] font-bold transition-all ${p === 1 ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-background border border-border-main text-muted hover:border-primary hover:text-primary'}`}>
                                    {p}
                                </button>
                            ))}
                        </div>
                        <div className="h-4 w-px bg-border-main"></div>
                        <div className="flex items-center gap-2">
                            <button disabled className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest disabled:opacity-30 hover:bg-background transition-all">Prev</button>
                            <button disabled className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest disabled:opacity-30 hover:bg-background transition-all">Next</button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
